// src/components/MapView/TopGeographies.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDown, ChevronUp, Trophy, MapPin } from "lucide-react";

import { useTopGeographies, useDisplayConfig, useTierConfig } from "../../hooks";
import { setSelectedGeography, setMapView, selectSelectedGeographyId } from "../../store/mapSlice";

/**
 * TopGeographies component - shows the top-ranked geographies
 */
const TopGeographies = () => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(true);
  const [showCount, setShowCount] = useState(10);
  
  const topGeographies = useTopGeographies(showCount);
  const selectedGeographyId = useSelector(selectSelectedGeographyId);
  
  const { geographyLabels } = useDisplayConfig();
  const { getTierColor } = useTierConfig();

  const handleSelect = (geography) => {
    const geoId = geography.geoId;
    if (geoId) {
      dispatch(setSelectedGeography(geoId));
      
      // Center map on selected geography if coordinates available
      if (geography.coordinates) {
        dispatch(setMapView({
          center: [geography.coordinates.lat, geography.coordinates.lng],
          zoom: 12,
        }));
      }
    }
  };

  const handleShowMore = () => {
    setShowCount((prev) => Math.min(prev + 10, 50));
  };

  const handleShowLess = () => {
    setShowCount(10);
  };

  if (topGeographies.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition"
      >
        <div className="flex items-center gap-2">
          <Trophy size={16} className="text-yellow-500" />
          <h3 className="font-semibold text-gray-800">
            Top {geographyLabels.plural}
          </h3>
          <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
            by rank
          </span>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {/* List */}
      {expanded && (
        <div>
          <ul className="divide-y">
            {topGeographies.map((geography, index) => {
              const geoId = geography.geoId;
              const isSelected = geoId === selectedGeographyId;
              const tier = geography.tier;
              const tierColor = getTierColor(tier);

              return (
                <li key={geoId || index}>
                  <button
                    onClick={() => handleSelect(geography)}
                    className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition text-left ${
                      isSelected ? 'bg-brand-50 border-l-4 border-secondary' : ''
                    }`}
                  >
                    {/* Rank badge */}
                    <div 
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        geography.rank <= 3 ? 'bg-yellow-100' : 'bg-gray-100'
                      }`}
                    >
                      <span className={`text-sm font-bold ${
                        geography.rank <= 3 ? 'text-yellow-700' : 'text-gray-600'
                      }`}>
                        {geography.rank}
                      </span>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <MapPin size={12} className="text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-gray-900 truncate">
                          {geography.city || geography.geoId}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {geography.zcta || geography.geoId}
                        {geography.county && ` • ${geography.county.replace(' Planning Region', '')}`}
                      </span>
                    </div>

                    {/* Score badge */}
                    <div className="flex-shrink-0 text-right">
                      <div
                        className="inline-flex items-center justify-center min-w-[40px] h-7 px-2 rounded text-sm font-bold text-white"
                        style={{ backgroundColor: tierColor }}
                      >
                        {geography.score}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {tier}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Show more/less buttons */}
          <div className="flex border-t">
            {showCount < 50 && topGeographies.length === showCount && (
              <button
                onClick={handleShowMore}
                className="flex-1 px-4 py-2 text-sm text-secondary hover:bg-gray-50 transition font-medium"
              >
                Show more
              </button>
            )}
            {showCount > 10 && (
              <button
                onClick={handleShowLess}
                className="flex-1 px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 transition border-l"
              >
                Show less
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TopGeographies;
